// Phase 43.18 — Admin Refunds Center
// Phase 43.54 — Distingue rimborsi cash vs ripristino corsa abbonamento.
// Lista refund pending/failed + modale che mostra chiaramente tipo pagamento.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle2, AlertTriangle, Banknote, Receipt,
  CreditCard, Package, Repeat, Wallet,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime } from "@/lib/format";

const KIND_META = {
  single: {
    label: "Singolo",
    Icon: CreditCard,
    pill: "bg-blue-50 text-blue-700 border-blue-200",
    action: "Rimborso CHF",
  },
  subscription_full: {
    label: "Abbonamento",
    Icon: Repeat,
    pill: "bg-violet-50 text-violet-700 border-violet-200",
    action: "Ripristino 1 corsa",
  },
  subscription_partial: {
    label: "Abbonamento + extra",
    Icon: Repeat,
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    action: "CHF + ripristino corsa",
  },
  package: {
    label: "Pacchetto corse",
    Icon: Package,
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    action: "Ripristino 1 corsa",
  },
  wallet: {
    label: "Credito wallet",
    Icon: Wallet,
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    action: "Riaccredito wallet",
  },
};

function KindBadge({ kind, label }) {
  const meta = KIND_META[kind] || KIND_META.single;
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.pill}`}
      data-testid={`refund-kind-badge-${kind}`}
    >
      <Icon className="h-3 w-3" />
      {label || meta.label}
    </span>
  );
}

export default function AdminRefunds() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [activeRefund, setActiveRefund] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/refunds/pending");
      setItems(data.items || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const processRefund = async (orderId, body) => {
    setWorking(orderId);
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/refund`, body);
      // Toast adattivo in base al kind/mode
      const cashRequested = data.cash?.performed && (data.cash?.amount || 0) > 0;
      const cashStatus = data.cash?.status || data.status;
      const cashOk = cashRequested && cashStatus === "succeeded";
      const creditOk = data.credit?.performed && data.credit?.restored_at;
      const walletCredit = data.kind === "wallet";
      if (data.refund_review_required || (cashRequested && !["pending", "requires_action", "failed", "canceled", "succeeded"].includes(cashStatus))) {
        toast.error("Risposta rimborso da verificare con l’assistenza. Nessuna conferma automatica.");
      } else if (cashRequested && ["pending", "requires_action"].includes(cashStatus)) {
        toast.info("Richiesta rimborso registrata: in attesa della conferma Stripe.");
      } else if (cashRequested && ["failed", "canceled"].includes(cashStatus)) {
        toast.error(`Rimborso non completato: ${data.cash?.error || cashStatus}`);
      } else if (data.cash?.already_refunded && !creditOk) {
        toast(`Già rimborsato (${data.cash.refund_id || "—"})`);
      } else if (cashOk && creditOk) {
        toast.success(
          `Rimborso ${formatCHF(data.cash.amount)} + ${walletCredit ? "credito wallet ripristinato" : "1 corsa ripristinata"}`
        );
      } else if (cashOk) {
        toast.success(
          data.cash.simulated
            ? `Rimborso simulato OK · ${formatCHF(data.cash.amount)}`
            : `Rimborso Stripe OK · ${formatCHF(data.cash.amount)}`
        );
      } else if (creditOk) {
        toast.success(walletCredit ? "Credito wallet ripristinato" : "1 corsa ripristinata al piano del cliente");
      } else if (data.ok) {
        toast.success("Rimborso processato");
      } else {
        toast.error(`Rimborso fallito: ${data.cash?.error || data.credit?.error || data.error || data.status}`);
      }
      setActiveRefund(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(null);
    }
  };

  // Totale CHF = somma solo dei cash_refund_chf (solo soldi reali da rimborsare)
  const totalCashChf = items.reduce((s, i) => s + (i.cash_refund_chf || 0), 0);
  const totalCredits = items.filter((i) => i.restore_inclusion).length;

  return (
    <div className="space-y-5" data-testid="admin-refunds-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Finance · Centro operativo</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Rimborsi</h1>
          <p className="text-sm text-dapnow-muted mt-1">
            Ordini annullati: rimborsi in CHF, riaccrediti wallet e ripristini di corse.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-refunds-reload"
          className="inline-flex items-center gap-2 rounded-lg border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold hover:border-dapnow-blue disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label="In coda"
          value={items.filter((i) => i.refund_status === "pending").length}
          icon={<Banknote className="h-4 w-4" />}
          kind="info"
        />
        <KpiCard
          label="Falliti"
          value={items.filter((i) => i.refund_status === "failed").length}
          icon={<AlertTriangle className="h-4 w-4" />}
          kind="warn"
        />
        <KpiCard
          label="Totale CHF"
          value={formatCHF(totalCashChf)}
          icon={<Receipt className="h-4 w-4" />}
          kind="ink"
        />
        <KpiCard
          label="Corse da ripristinare"
          value={totalCredits}
          icon={<Repeat className="h-4 w-4" />}
          kind="violet"
        />
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: "1200px" }}>
            <thead className="bg-dapnow-bg text-left">
              <tr>
                <Th>Ordine</Th>
                <Th>Cliente</Th>
                <Th>Tipo pagamento</Th>
                <Th>Pagato</Th>
                <Th>Rimborso CHF</Th>
                <Th>Azione</Th>
                <Th>Stato</Th>
                <Th>Annullato</Th>
                <Th className="text-right">—</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="p-10 text-center text-dapnow-muted">Caricamento…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan="9" className="p-10 text-center text-dapnow-muted">
                  <CheckCircle2 className="h-6 w-6 inline mr-2 text-emerald-600" />
                  Nessun rimborso da processare. Tutto a posto.
                </td></tr>
              )}
              {items.map((it) => {
                const kindMeta = KIND_META[it.refund_kind] || KIND_META.single;
                return (
                  <tr key={it.id} className="border-t border-dapnow-line" data-testid={`admin-refund-row-${it.id}`}>
                    <Td className="font-mono text-xs">
                      #{it.id.slice(0, 8).toUpperCase()}
                      {it.payment_simulated && <span className="ml-2 text-[10px] inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">SIM</span>}
                    </Td>
                    <Td className="font-semibold">{it.customer_name || "—"}</Td>
                    <Td><KindBadge kind={it.refund_kind} label={it.refund_kind_label} /></Td>
                    <Td className="tabular-nums">{formatCHF(it.gross_price)}</Td>
                    <Td className={`font-semibold tabular-nums ${it.cash_refund_chf > 0 ? "text-emerald-700" : "text-dapnow-muted"}`}>
                      {it.cash_refund_chf > 0 ? formatCHF(it.cash_refund_chf) : "—"}
                    </Td>
                    <Td className="text-xs">
                      <span className="font-semibold">{kindMeta.action}</span>
                      {it.restore_inclusion && it.cash_refund_chf > 0 && (
                        <span className="block text-dapnow-muted">CHF + 1 corsa</span>
                      )}
                    </Td>
                    <Td>
                      {it.refund_status === "pending" && <span className="pill bg-amber-100 text-amber-800">In attesa</span>}
                      {it.refund_status === "failed" && (
                        <span className="pill bg-rose-100 text-rose-800" title={it.refund_error}>Fallito</span>
                      )}
                    </Td>
                    <Td className="text-xs text-dapnow-muted">{formatDateTime(it.cancelled_at)}</Td>
                    <Td className="text-right">
                      <button
                        onClick={() => setActiveRefund(it)}
                        disabled={working === it.id}
                        data-testid={`admin-refund-open-${it.id}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-dapnow-ink text-white px-3 h-8 text-xs font-semibold hover:bg-dapnow-blue transition disabled:opacity-50"
                      >
                        Processa
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {activeRefund && (
        <RefundConfirmModal
          item={activeRefund}
          working={working === activeRefund.id}
          onClose={() => setActiveRefund(null)}
          onConfirm={(body) => processRefund(activeRefund.id, body)}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, icon, kind }) {
  const palette =
    kind === "warn" ? "bg-amber-50 border-amber-200 text-amber-900"
    : kind === "ink" ? "bg-dapnow-ink text-white border-dapnow-ink"
    : kind === "violet" ? "bg-violet-50 border-violet-200 text-violet-900"
    : "bg-blue-50 border-blue-200 text-blue-900";
  return (
    <div className={`rounded-2xl border p-4 min-w-0 ${palette}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="overline text-[10px] truncate">{label}</p>
        <div className="shrink-0 opacity-80">{icon}</div>
      </div>
      <p className="font-display text-xl sm:text-2xl font-extrabold mt-2 truncate tabular-nums" title={String(value)}>
        {value}
      </p>
    </div>
  );
}

function RefundConfirmModal({ item, working, onClose, onConfirm }) {
  const kindMeta = KIND_META[item.refund_kind] || KIND_META.single;
  const KindIcon = kindMeta.Icon;
  const walletCredit = item.refund_kind === "wallet";
  const creditRestoredAt = walletCredit ? item.wallet_credit_restored_at
    : item.refund_kind === "package" ? item.pack_credit_restored_at : item.inclusion_restored_at;
  // Mode di default = auto. L'admin può override.
  const [mode, setMode] = useState("auto");
  const [amount, setAmount] = useState(String(item.cash_refund_chf || 0));
  const [reason, setReason] = useState(item.cancel_reason || "");

  // Mode "auto" applica esattamente classification.refund_action
  const effectiveAction = mode === "auto" ? item.refund_action : mode;
  const willCash = effectiveAction === "cash" || effectiveAction === "both";
  const willCredit = effectiveAction === "credit" || effectiveAction === "both" || (walletCredit && willCash);

  const canConfirm = willCash
    ? amount && parseFloat(amount) > 0
    : willCredit; // pure credit: sempre ok

  const handleConfirm = () => {
    const body = { mode };
    if (willCash) {
      body.amount_chf = parseFloat(amount);
    }
    if (reason) body.reason = reason;
    onConfirm(body);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} data-testid="admin-refund-modal-backdrop" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto max-h-[90vh] overflow-y-auto" data-testid="admin-refund-modal">
          <div className="border-b border-dapnow-line px-5 py-4">
            <p className="overline text-[10px]">Processa rimborso</p>
            <p className="font-display text-lg font-bold tracking-tight font-mono">
              #{item.id.slice(0, 8).toUpperCase()}
            </p>
            <p className="text-xs text-dapnow-muted mt-1">{item.customer_name || "—"}</p>
          </div>
          <div className="p-5 space-y-4 text-sm">

            {/* ── Banner tipo pagamento ─────────────────────────────────── */}
            <div className={`rounded-xl border ${kindMeta.pill} p-3`} data-testid="refund-kind-banner">
              <div className="flex items-center gap-2">
                <KindIcon className="h-4 w-4 shrink-0" />
                <p className="overline text-[10px]">Tipo pagamento</p>
              </div>
              <p className="font-semibold mt-1">{item.refund_kind_label || kindMeta.label}</p>
              <p className="text-xs mt-1 opacity-80">{item.refund_explanation}</p>
            </div>

            {/* ── Azione che verrà eseguita ─────────────────────────────── */}
            <div className="rounded-xl border border-dapnow-line p-3 bg-dapnow-bg">
              <p className="overline text-[10px]">Azione che verrà eseguita</p>
              <div className="mt-2 space-y-1.5">
                {willCash ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Wallet className="h-4 w-4 text-emerald-700" />
                    <span className="font-semibold">Rimborso CHF</span>
                    <span className="ml-auto font-bold tabular-nums">{formatCHF(parseFloat(amount) || 0)}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-dapnow-muted line-through">
                    <Wallet className="h-4 w-4" />
                    <span>Rimborso CHF</span>
                    <span className="ml-auto">—</span>
                  </div>
                )}
                {willCredit ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Repeat className="h-4 w-4 text-violet-700" />
                    <span className="font-semibold">{walletCredit ? "Riaccredito wallet" : "Ripristino 1 corsa al piano"}</span>
                    <span className="ml-auto text-[11px] text-dapnow-muted">
                      {walletCredit
                        ? (creditRestoredAt ? "già riaccreditato" : "da riaccreditare")
                        : (creditRestoredAt ? "già ripristinata" : "da ripristinare")}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-dapnow-muted line-through">
                    <Repeat className="h-4 w-4" />
                    <span>{walletCredit ? "Riaccredito wallet" : "Ripristino corsa"}</span>
                    <span className="ml-auto">—</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Mode override (advanced) ─────────────────────────────── */}
            <div>
              <p className="overline text-[10px] mb-1.5">Modalità</p>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                {["auto", "cash", "credit", "both"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    data-testid={`refund-mode-${m}`}
                    className={`rounded-lg border px-2 py-1.5 font-semibold transition ${
                      mode === m
                        ? "border-dapnow-ink bg-dapnow-ink text-white"
                        : "border-dapnow-line bg-white hover:border-dapnow-blue"
                    }`}
                  >
                    {m === "auto" && "Automatico"}
                    {m === "cash" && (walletCredit ? "CHF (wallet incluso)" : "Solo CHF")}
                    {m === "credit" && (walletCredit ? "Solo wallet" : "Solo corsa")}
                    {m === "both" && (walletCredit ? "CHF + wallet" : "CHF + corsa")}
                  </button>
                ))}
              </div>
              {mode !== "auto" && (
                <p className="text-[11px] text-amber-700 mt-1.5">
                  Modalità override: stai forzando un'azione diversa da quella suggerita.
                </p>
              )}
            </div>

            {/* ── Cifre breakdown ─────────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="border border-dapnow-line rounded-lg p-2">
                <p className="overline text-[10px]">Pagato</p>
                <p className="font-semibold mt-0.5 tabular-nums">{formatCHF(item.gross_price)}</p>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-2">
                <p className="overline text-[10px] text-emerald-700">Rimborso CHF suggerito</p>
                <p className="font-semibold mt-0.5 text-emerald-900 tabular-nums">{formatCHF(item.cash_refund_chf || 0)}</p>
              </div>
              <div className="border border-dapnow-line rounded-lg p-2">
                <p className="overline text-[10px]">Penale</p>
                <p className="font-semibold mt-0.5 tabular-nums">{formatCHF(item.penalty_chf)}</p>
              </div>
              <div className="border border-dapnow-line rounded-lg p-2">
                <p className="overline text-[10px]">Sub-stato</p>
                <p className="font-semibold mt-0.5 break-words">{item.cancellation_sub_status || "—"}</p>
              </div>
            </div>

            {/* ── Importo override (solo se cash) ─────────────────────── */}
            {willCash && (
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">
                  Importo da rimborsare (CHF)
                </span>
                <input
                  type="number" step="0.05" min="0"
                  data-testid="admin-refund-amount"
                  className="input-tall w-full"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
            )}

            <label className="block">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">Motivo (opzionale)</span>
              <textarea
                data-testid="admin-refund-reason"
                className="input-tall w-full min-h-[60px]"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Visibile nello storico admin"
              />
            </label>

            {item.payment_simulated && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
                Ordine pagato in modalità <strong>simulata</strong>: nessuna chiamata reale a Stripe verrà fatta.
              </div>
            )}

            {item.refund_error && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 text-xs text-rose-900" data-testid="admin-refund-prev-error">
                <p className="font-semibold mb-0.5">Tentativo precedente fallito:</p>
                <p className="font-mono break-all">{item.refund_error}</p>
              </div>
            )}
          </div>
          <div className="border-t border-dapnow-line px-5 py-3 flex items-center justify-end gap-2 bg-white sticky bottom-0">
            <button
              onClick={onClose}
              data-testid="admin-refund-cancel"
              className="rounded-lg border border-dapnow-line px-3 h-10 text-sm font-semibold hover:bg-dapnow-bg"
            >
              Annulla
            </button>
            <button
              onClick={handleConfirm}
              disabled={working || !canConfirm}
              data-testid="admin-refund-confirm"
              className="inline-flex items-center gap-2 rounded-lg bg-dapnow-ink text-white px-4 h-10 text-sm font-semibold disabled:opacity-50"
            >
              {working
                ? "Processando…"
                : willCash && willCredit
                  ? (walletCredit ? "Conferma rimborso + wallet" : "Conferma rimborso + corsa")
                  : willCash
                    ? "Conferma rimborso CHF"
                    : willCredit
                      ? (walletCredit ? "Riaccredita wallet" : "Ripristina 1 corsa")
                      : "Conferma"
              }
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Th({ children, className = "" }) { return <th className={`px-4 py-3 overline text-[10px] font-semibold ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
